/**
 * @module app.shared
 */

import { bindingMode, autoinject, inlineView, BindingEngine, Disposable } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MaterializeComponentRefresher } from 'app/shared/lib/materialize/component-refresher';
import { oneOf } from 'app/shared/lib/utilities';
import { getKey, Key } from 'app/shared/lib/keys';
import { guid } from 'app/shared/lib/guid';
import { isElementClickable, isElementInteractive, setAttributeIfNotExist, setAttributesIfNotExist } from 'app/shared/lib/dom';

export interface ITooltip {
    refresh(): void;
}

type MaterializeTooltip = M.Tooltip & {
    tooltipEl: Element
}

@autoinject
@inlineView('<template class="screen-reader-only"><slot></slot></template>')
export class EssTooltipCustomElement {

    ////****************** Fields and Properties ************************************////
    @bindable.any({ defaultBindingMode: bindingMode.oneTime })
    public targetElement: any = null;

    @bindable.string({ defaultBindingMode: bindingMode.toView, changeHandler: 'positionChanged' })
    public position: string = 'bottom';

    @bindable.boolean({ defaultBindingMode: bindingMode.toView, changeHandler: 'disabledChanged' })
    public disabled: boolean = false;

    @bindable.any({ defaultBindingMode: bindingMode.toView, changeHandler: 'refreshStateChanged' })
    public refreshState: any = null;

    private isActive: boolean = false;  // Tracks user activity as they move from the target to the tooltip

    private get tooltip(): MaterializeTooltip | null {
        return this.refresher.component;
    }
    private guid = guid();
    private tooltipId: string = `${this.guid}-tooltip`;

    protected tooltipOpenSubscription: Disposable | null = null;

    ////****************** Element References ***************************************////
    private $tooltipContent: Element | null = null;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly element: Element,
        private readonly refresher: MaterializeComponentRefresher<MaterializeTooltip>,
        private readonly bindingEngine: BindingEngine
    ) {
        refresher.configure({
            childList: true,
            subtree: true,
            characterData: true
        });
    }

    protected attached() {
        const target = this.getTargetElement();
        this.refresher.initialize(() => {
            // Init
            if(!target) { return null; }
            target.classList.add('ess-tooltip');

            const options: Partial<M.TooltipOptions> = {
                html: this.element.innerHTML,
                enterDelay: 500,
                exitDelay: 100,
                inDuration: 100,
                outDuration: 500,
                margin: 2,
                transitionMovement: 5,
                position: oneOf(this.position, ['bottom', 'top', 'left', 'right'])
            };

            const instance = M.Tooltip.init(target, options) as MaterializeTooltip;

            // Observe Changes to the Tooltip's `isOpen` property
            this.tooltipOpenSubscription = this.bindingEngine
                .propertyObserver(instance, 'isOpen')
                .subscribe((newValue, oldValue) => {
                    this.tooltipOpenChanged(newValue, oldValue);
                });

            this.updateTooltipContentElement(instance);
            this.updateTargetElement();

            return instance;
        }, () => {
            // Terminate
            this.tooltipOpenSubscription?.dispose();
            this.tooltipOpenSubscription = null;
            this.removeTooltipHandlers();
            // ESS-15233 temporarily comment out the 'aria-describedby' logic
            // if(target?.getAttribute('aria-describedby') == this.tooltipId) { target.removeAttribute('aria-describedby'); }
        }, this.disabled);

        if(!target) { return; }
        target.addEventListener("keydown", this.handleKeyDown.bind(this));
        target.addEventListener("mouseover", this.handleMouseOver.bind(this));
        target.addEventListener("mouseleave", this.handleMouseLeave.bind(this), { capture: true });
    }

    protected detached() {
        this.tooltipOpenSubscription?.dispose();
        this.cleanupTargetElement();
        this.removeTargetHandlers();
        this.refresher.destroy();
    }

    ////****************** Functions ************************************************////
    public async refresh() {
        await this.refresher.refresh();
    }

    protected getTargetElement(): Element | null {
        const target = this.targetElement || this.element.parentElement || this.element;
        this.targetElement = target;
        return target instanceof Element && target.isConnected ? target : null;
    }

    protected positionChanged(newValue: string, oldValue: string): void {
        if(newValue !== oldValue) {
            this.refresh();
        }
    }

    protected disabledChanged(newValue: boolean, oldValue: boolean): void {
        if(newValue !== oldValue) {
            if(this.disabled) {
                this.cleanupTargetElement();
                this.refresher.disable()
            }
            else {
                this.refresher.enable();
            }
        }
    }

    protected refreshStateChanged(newValue: any, oldValue: any): void {
        if(newValue !== oldValue) {
            this.refresh();
        }
    }

    /** Handles changes to the Tooltip's `isOpen` property */
    protected tooltipOpenChanged(newValue: any, oldValue: any): void {
        if(!this.tooltip) { return; }
        if(newValue) {
            // Tooltip Is Open\Visible
            this.tooltip.tooltipEl.classList.remove('tooltip-hidden');
        }
        else {
            this.tooltip.tooltipEl.classList.add('tooltip-hidden');
        }
    }

    /** Update Materialize's Tooltip Content element */
    private updateTooltipContentElement(tooltip: MaterializeTooltip | null) {
        if(!tooltip) { return; }

        if(tooltip.tooltipEl) {
            this.$tooltipContent = tooltip.tooltipEl.querySelector('.tooltip-content');
        }

        this.tooltipId = this.$tooltipContent?.id || (tooltip.el.id ? `${tooltip.el.id}-tooltip` : `${this.guid}-tooltip`);

        if(this.$tooltipContent) {
            setAttributesIfNotExist(this.$tooltipContent, { 'id': this.tooltipId, 'role': 'tooltip' });
        }

        this.$tooltipContent?.addEventListener("mouseover", this.handleTooltipMouseOver.bind(this), { capture: true });
        this.$tooltipContent?.addEventListener("mouseleave", this.handleTooltipMouseLeave.bind(this), { capture: true });
    }

    /** Update the Tooltip's target element */
    private updateTargetElement(): void {
        const target = this.getTargetElement();
        if(!target) { return; }
        const isClickable: boolean = isElementClickable(<HTMLElement>target);
        const isInteractive: boolean = isElementInteractive(<HTMLElement>target);

        // If the element has user interactions, ensure the element is tabbable
        if(isClickable) {
            setAttributeIfNotExist(target, 'tabIndex', '0');
        }

        // If the element has user interactions, set a button role when it is required
        if(isClickable && !isInteractive) {
            setAttributeIfNotExist(target, 'role', 'button');
        }

        // If the element has user interactions, set a screen reader description
        // ESS-15233 temporarily comment out the 'aria-describedby' logic
        // if(isClickable && !(target.getAttribute('aria-description') || target.getAttribute('aria-describedby'))) {
        //     target.setAttribute('aria-describedby', this.tooltipId);
        // }
    }

    private cleanupTargetElement() {
        if(this.refresher.component && this.refresher.component.el) {
            this.refresher.component.el.classList.remove('ess-tooltip');
        }
    }

    private removeTargetHandlers() {
        const target = this.getTargetElement();
        if(!target) { return; }
        target.removeEventListener("keydown", this.handleKeyDown.bind(this));
        target.removeEventListener("mouseover", this.handleMouseOver.bind(this));
        target.removeEventListener("mouseleave", this.handleMouseLeave.bind(this), { capture: true });
    }

    private removeTooltipHandlers() {
        this.$tooltipContent?.removeEventListener("mouseover", this.handleTooltipMouseOver.bind(this), { capture: true });
        this.$tooltipContent?.removeEventListener("mouseleave", this.handleTooltipMouseLeave.bind(this), { capture: true });
    }

    protected handleKeyDown(event: KeyboardEvent): boolean {
        const key = getKey(event);
        switch(key) {
            case Key.Esc:
                this.closeTooltip();
                return false;
            default:
                return true;
        }
    }

    protected handleMouseOver(event: MouseEvent): void {
        this.isActive = true;
    }

    protected handleMouseLeave(event: MouseEvent): void {
        event.stopImmediatePropagation();
        this.isActive = false;
        setTimeout(this.closeTooltip.bind(this), 200);
    }

    protected handleTooltipMouseOver(event: MouseEvent): void {
        //mouse moves away from the element and enters the tooltip
        this.isActive = true;
    }

    protected handleTooltipMouseLeave(event: MouseEvent): void {
        this.isActive = false;
        if(this.tooltip?.isOpen) { setTimeout(this.closeTooltip.bind(this), 200); }
    }

    protected closeTooltip(): void {
        if(!this.isActive) {
            this.tooltip?.close();
        }
    }

}
